import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Shared Components
import Login from './components/Login';
import PlatformSelector from './components/PlatformSelector';
import StoreSelector from './components/StoreSelector';
import SkuMapping from './components/sku/SkuMapping';

// Shopify Components
import ShopifyDashboard from './components/shopify/Dashboard';
import ShopifyProductList from './components/shopify/ProductList';
import ShopifyOrderList from './components/shopify/OrderList';
import ShopifyReports from './components/shopify/Reports';
import ShopifySettings from './components/shopify/Settings';
import ShopifySidebar from './components/shopify/Sidebar';

// eBay Components
import EbayDashboard from './components/ebay/Dashboard';
import EbayProductList from './components/ebay/ProductList';
import EbayOrderList from './components/ebay/OrderList';
import EbayReports from './components/ebay/Reports';
import EbaySettings from './components/ebay/Settings';
import EbaySidebar from './components/ebay/Sidebar';

// Amazon Components
import AmazonDashboard from './components/amazon/Dashboard';
import AmazonProductList from './components/amazon/ProductList';
import AmazonOrderList from './components/amazon/OrderList';
import AmazonReports from './components/amazon/Reports';
import AmazonSettings from './components/amazon/Settings';
import AmazonSidebar from './components/amazon/Sidebar';

// ✅ Ledger Components
import LedgerDashboard from './components/ledger/Ledgerdashboard';
import LedgerSidebar from './components/ledger/LedgerSidebar';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const handleLogin = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setSelectedPlatform(null);
    setSelectedStore(null);
  };

  const handleSelectPlatform = (platform) => {
    setSelectedPlatform(platform);
    if (platform === 'shopify') {
      setSelectedStore({ id: 1, store_name: 'Shopify Store', platform: 'shopify' });
    }
    // ✅ Ledger needs no store selection
  };

  const handleBackToPlatforms = () => {
    setSelectedPlatform(null);
    setSelectedStore(null);
  };

  const handleBackToStores = () => {
    setSelectedStore(null);
  };

  const handleSelectStore = (store) => {
    setSelectedStore({ ...store, platform: selectedPlatform });
  };

  if (!isAuthenticated) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  if (!selectedPlatform) {
    return (
      <>
        <PlatformSelector onSelectPlatform={handleSelectPlatform} />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  if ((selectedPlatform === 'amazon' || selectedPlatform === 'ebay') && !selectedStore) {
    return (
      <>
        <StoreSelector
          platform={selectedPlatform}
          onBack={handleBackToPlatforms}
          onSelectStore={handleSelectStore}
        />
        <ToastContainer position="top-right" autoClose={3000} />
      </>
    );
  }

  const renderPlatformApp = () => {
    // ✅ LEDGER PLATFORM
    if (selectedPlatform === 'ledger') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <LedgerSidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"          element={<LedgerDashboard activeTab="ledger"    />} />
                <Route path="/transactions" element={<LedgerDashboard activeTab="transactions" />} />
                <Route path="/statement"    element={<LedgerDashboard activeTab="statement"    />} />
                <Route path="/reports"      element={<LedgerDashboard activeTab="reports"      />} />
                <Route path="*"             element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'shopify') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <ShopifySidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<ShopifyDashboard />} />
                <Route path="/products"    element={<ShopifyProductList />} />
                <Route path="/orders"      element={<ShopifyOrderList />} />
                <Route path="/reports"     element={<ShopifyReports />} />
                <Route path="/sku-mapping" element={<SkuMapping />} />
                <Route path="/settings"    element={<ShopifySettings />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'ebay') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <EbaySidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
              onBackToStores={handleBackToStores}
              storeName={selectedStore?.store_name}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<EbayDashboard    storeId={selectedStore?.id} />} />
                <Route path="/products"    element={<EbayProductList  storeId={selectedStore?.id} />} />
                <Route path="/orders"      element={<EbayOrderList    storeId={selectedStore?.id} />} />
                <Route path="/reports"     element={<EbayReports      storeId={selectedStore?.id} />} />
                <Route path="/sku-mapping" element={<SkuMapping />} />
                <Route path="/settings"    element={<EbaySettings     storeId={selectedStore?.id} />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }

    if (selectedPlatform === 'amazon') {
      return (
        <Router>
          <div className="flex h-screen bg-gray-100">
            <AmazonSidebar
              onLogout={handleLogout}
              onBackToPlatforms={handleBackToPlatforms}
              onBackToStores={handleBackToStores}
              storeName={selectedStore?.store_name}
            />
            <div className="flex-1 overflow-auto">
              <Routes>
                <Route path="/"            element={<AmazonDashboard   storeId={selectedStore?.id} />} />
                <Route path="/products"    element={<AmazonProductList storeId={selectedStore?.id} />} />
                <Route path="/orders"      element={<AmazonOrderList   storeId={selectedStore?.id} />} />
                <Route path="/reports"     element={<AmazonReports     storeId={selectedStore?.id} />} />
                <Route path="/sku-mapping" element={<SkuMapping />} />
                <Route path="/settings"    element={<AmazonSettings    storeId={selectedStore?.id} />} />
                <Route path="*"            element={<Navigate to="/" />} />
              </Routes>
            </div>
          </div>
        </Router>
      );
    }
  };

  return (
    <>
      {renderPlatformApp()}
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}

export default App;